// Marketing data for /integrations. Every claim here is grounded in the
// shipped product: accounting export (QuickBooks/Xero), Stripe billing,
// per-member Google Calendar push sync, ForeFlight logbook import, and
// Airbly and AirSync flight data recorders.
//
// An entry with `comingSoon: true` gets a directory row and a badge, but
// no landing page and no sitemap URL - LIVE_INTEGRATIONS is the list that
// gets linked.

export const INTEGRATIONS = [
  {
    slug: 'quickbooks',
    name: 'QuickBooks Online',
    logo: '/marketing/logos/quickbooks.svg',
    logoHeight: 22,
    category: 'Accounting',
    managedBy: 'Organization admin',
    setup: 'About 10 minutes',
    tagline: 'Your monthly close, already in QuickBooks.',
    overview: [
      'Resyrv is the system of record for what happened on the flight line: who flew, what it cost, and who paid. QuickBooks Online is where your accountant lives. The integration keeps both true without anyone re-keying a number.',
      'Connect once with OAuth, choose the accounts your records map to, and the financial records you choose to export - invoices, payments, and customers - flow to QuickBooks with the month-end close.',
    ],
    syncs: [
      'Member invoices and statements',
      'Payments and their allocations',
      'Members as QuickBooks customers',
      'Monthly summaries your CPA can work from',
    ],
    steps: [
      { title: 'Connect', body: 'An organization admin signs in to QuickBooks from Resyrv settings - standard OAuth, no credentials stored in Resyrv.' },
      { title: 'Map accounts', body: 'Choose which QuickBooks accounts receive flight revenue, dues, and payments. Sensible defaults are suggested.' },
      { title: 'Export', body: 'Run the export at month-end close, or whenever your books need to catch up. Every export is itemized and repeatable.' },
    ],
    faq: [
      { q: 'Does Resyrv write to my books automatically?', a: 'Exports run when you trigger them, so your accountant reviews before anything lands. Nothing writes to QuickBooks behind your back.' },
      { q: 'What if we already invoice from QuickBooks?', a: 'Most groups flip it: Resyrv computes the charges from actual flights and reserves, and QuickBooks receives the results. That removes the spreadsheet between the flight line and the books.' },
    ],
    docLink: '/blog/connect-quickbooks-online',
    docLabel: 'Setup guide: connecting QuickBooks Online',
  },
  {
    slug: 'xero',
    name: 'Xero',
    logo: '/marketing/logos/xero.svg',
    logoHeight: 30,
    category: 'Accounting',
    managedBy: 'Organization admin',
    setup: 'About 10 minutes',
    tagline: 'Flight-line records, reconciled in Xero.',
    overview: [
      'For groups that keep their books in Xero, Resyrv exports the same financial records it computes from real flying: invoices, payments, and member records, mapped to the Xero accounts you choose.',
      'The connection is OAuth from organization settings; exports are explicit, itemized, and repeatable, so the books never depend on somebody remembering a spreadsheet step.',
    ],
    syncs: [
      'Member invoices and statements',
      'Payments and their allocations',
      'Members as Xero contacts',
      'Month-end summaries for reconciliation',
    ],
    steps: [
      { title: 'Connect', body: 'An organization admin authorizes Xero from Resyrv settings via OAuth.' },
      { title: 'Map accounts', body: 'Point revenue, dues, and payment records at the right Xero accounts.' },
      { title: 'Export', body: 'Export at close, review in Xero, reconcile as usual.' },
    ],
    faq: [
      { q: 'Can we switch from QuickBooks to Xero later?', a: 'Yes - the export layer is the same. Disconnect one provider, connect the other, and remap accounts.' },
      { q: 'Who can run exports?', a: 'Only organization roles with billing administration permissions. Members never see the accounting connection.' },
    ],
    docLink: '/blog/connect-xero',
    docLabel: 'Setup guide: connecting Xero',
  },
  {
    slug: 'stripe',
    name: 'Stripe',
    logo: '/marketing/logos/stripe.svg',
    logoHeight: 22,
    category: 'Payments',
    managedBy: 'Organization admin',
    setup: 'Same day, including verification',
    tagline: 'Members pay online. Balances settle themselves.',
    overview: [
      'Stripe powers payments across Resyrv: members pay statements and dues by card or ACH, and every payment posts against the member ledger automatically, clearing open charges oldest-first.',
      'The treasurer stops being a collections agency. Charges come from actual flights and dues schedules; payments arrive attributed, allocated, and visible to both sides.',
    ],
    syncs: [
      'Card and ACH payments against member balances',
      'Recurring dues and plan subscriptions',
      'Automatic allocation to open charges',
      'Payouts to your organization\'s bank account',
    ],
    steps: [
      { title: 'Connect', body: 'Create or link a Stripe account from organization settings. Stripe handles identity and bank verification.' },
      { title: 'Set your policies', body: 'Choose what members can pay online - statements, dues, or both - and whether ACH is offered alongside cards.' },
      { title: 'Get paid', body: 'Members pay from their statement. The ledger updates in real time; funds land in your bank on Stripe\'s payout schedule.' },
    ],
    faq: [
      { q: 'Who pays the processing fees?', a: 'Stripe\'s standard fees apply to the organization; ACH is substantially cheaper than cards for large balances, which is why most groups enable it.' },
      { q: 'Does Resyrv store card numbers?', a: 'No. Card data is handled entirely by Stripe; Resyrv stores only the resulting payment records.' },
    ],
    docLink: '/docs/payments-and-refunds',
    docLabel: 'Docs: payments and refunds',
  },
  {
    slug: 'authorize-net',
    name: 'Authorize.net',
    logo: '/marketing/logos/authorize-net.svg',
    logoHeight: 20,
    category: 'Payments',
    managedBy: 'Organization admin',
    setup: 'Connect your existing gateway',
    tagline: 'Already on Authorize.net? Keep your gateway.',
    overview: [
      'Plenty of schools and clubs have run an Authorize.net merchant account for years, with rates they negotiated and a processor their bank knows. Resyrv does not make you throw that away: choose Authorize.net as your organization\'s payment processor and members pay their statements through the gateway you already have.',
      'Everything downstream behaves the same as any Resyrv payment: charges post against member ledgers, autopay cards are stored with the gateway, refunds flow back to the ledger, and webhook handling is de-duplicated so a gateway event can never post twice.',
    ],
    syncs: [
      'Member statement payments through your gateway',
      'Saved cards for autopay',
      'Refunds, posted back to the member ledger',
      'Gateway webhooks, processed exactly once',
    ],
    steps: [
      { title: 'Choose your processor', body: 'An organization admin selects Authorize.net as the payment processor and enters the gateway credentials.' },
      { title: 'Members pay normally', body: 'Statements and autopay run through your merchant account; members see no difference in the flow.' },
      { title: 'Ledger stays true', body: 'Payments, refunds, and failures post to the same member ledger as every other Resyrv transaction.' },
    ],
    faq: [
      { q: 'Why keep Authorize.net instead of Stripe?', a: 'Usually the rates: established operations often have negotiated pricing or a bank relationship worth preserving. Resyrv supports either processor per organization - pick whichever your accountant prefers.' },
      { q: 'Can we switch processors later?', a: 'Yes. The processor is an organization-level setting; historical ledger records are processor-agnostic, so past payments stay intact when you change.' },
    ],
    docLink: '/docs/payments-and-refunds',
    docLabel: 'Docs: payments and refunds',
  },
  {
    slug: 'cash-app',
    name: 'Cash App Pay',
    logo: '/marketing/logos/cash-app.svg',
    logoHeight: 26,
    category: 'Payments',
    managedBy: 'Each member',
    setup: 'Nothing - included with Stripe',
    tagline: 'The way younger members already pay for everything.',
    overview: [
      'Students and younger club members often do not reach for a card number - they reach for Cash App. With Stripe connected, Resyrv accepts Cash App Pay on member statements alongside cards and ACH.',
      'Members can even save Cash App Pay as their default payment method for autopay, so dues and flight charges settle themselves the same way a stored card would.',
    ],
    syncs: [
      'Statement payments via Cash App Pay',
      'Cash App Pay saved as an autopay method',
      'Payments posted to the member ledger automatically',
    ],
    steps: [
      { title: 'Connect Stripe', body: 'Cash App Pay rides on your organization\'s Stripe connection - no separate setup or account.' },
      { title: 'Members choose it', body: 'At checkout, Cash App Pay appears next to card and ACH. Members approve the payment in their Cash App.' },
      { title: 'Set and forget', body: 'Members who prefer it can make Cash App Pay their default for autopay.' },
    ],
    faq: [
      { q: 'Does the organization need a Cash App account?', a: 'No. Cash App Pay is a payment method within your Stripe connection; only the member needs the app.' },
      { q: 'What does it cost?', a: 'Stripe\'s standard processing applies, as with cards. Members pay nothing extra to use it.' },
    ],
    docLink: '/docs/payments-and-refunds',
    docLabel: 'Docs: payments and refunds',
  },
  {
    slug: 'google-calendar',
    name: 'Google Calendar',
    logo: '/marketing/logos/google-calendar.svg',
    logoHeight: 26,
    category: 'Scheduling',
    managedBy: 'Each member',
    setup: 'Under a minute',
    tagline: 'Your bookings, on the calendar you already check.',
    overview: [
      'Members connect their own Google account once, and their Resyrv reservations appear in their personal Google Calendar - created, updated, and removed as the schedule changes.',
      'It is a one-way push from Resyrv to the member\'s calendar: the schedule of record stays in Resyrv, where conflict detection and booking rules live, while reminders and visibility live where the member already looks every day.',
    ],
    syncs: [
      'Your reservations, pushed to your primary calendar',
      'Changes and cancellations, reflected automatically',
      'Aircraft and time details on every event',
    ],
    steps: [
      { title: 'Connect', body: 'Each member links Google from their own settings - individual OAuth, scoped to calendar events only.' },
      { title: 'Book normally', body: 'Reservations made in Resyrv appear in Google Calendar within moments.' },
      { title: 'Stay current', body: 'Move or cancel a booking and the calendar follows. Disconnect anytime; access revokes cleanly.' },
    ],
    faq: [
      { q: 'Can the whole club share one calendar?', a: 'Each member connects individually and receives their own bookings. The full-fleet picture lives in Resyrv\'s schedule, which every member can already see.' },
      { q: 'What permissions does Resyrv get?', a: 'Only the ability to write calendar events, plus your email to show which account is connected. Resyrv cannot read the rest of your calendar.' },
    ],
    docLink: '/docs/calendar-sync',
    docLabel: 'Docs: calendar sync',
  },
  {
    slug: 'foreflight',
    name: 'ForeFlight',
    logo: '/marketing/logos/foreflight.svg',
    logoHeight: 24,
    category: 'Pilot logbook',
    managedBy: 'Each pilot',
    setup: 'One import',
    tagline: 'Arrive with your logbook. Leave with it, too.',
    overview: [
      'Pilots arrive at Resyrv with a history, and much of it lives in ForeFlight. The logbook import brings that history across - flights, times, and landings - so your Resyrv logbook starts complete instead of starting over.',
      'Aircraft from your ForeFlight logbook are matched to Resyrv aircraft records, and unmatched ones are created for you to review, so imported hours land against the right tail numbers.',
      'It works in both directions, and that matters most at flight schools: a student logs their entire training in Resyrv, finishes the checkride, and exports the whole logbook in ForeFlight\'s own format - accepted by its importer as-is. Training hours belong to the pilot, and they leave with the pilot.',
    ],
    syncs: [
      'Logbook entries from a ForeFlight export, imported',
      'Times, landings, and flight details, both directions',
      'Aircraft records, matched by identifier',
      'Your full Resyrv logbook, exported in ForeFlight\'s format',
    ],
    steps: [
      { title: 'Export from ForeFlight', body: 'Download your logbook export from ForeFlight on the web.' },
      { title: 'Import to Resyrv', body: 'Upload it in your pilot logbook. Resyrv parses entries and maps aircraft.' },
      { title: 'Review', body: 'Confirm matched aircraft and spot-check entries. Your currency and totals include the full history from day one.' },
    ],
    faq: [
      { q: 'Can I take my logbook with me when I leave?', a: 'Yes, completely. Resyrv exports your logbook in ForeFlight\'s own CSV format, column for column, so ForeFlight\'s importer accepts it directly. Students who train at a Resyrv school graduate with their whole history ready for wherever they fly next.' },
      { q: 'Is this a live two-way sync?', a: 'It is import and export on your terms rather than a background sync: bring history in when you arrive, re-import newer exports as you go, and take everything with you whenever you choose.' },
      { q: 'What about other logbook apps?', a: 'Anything that can produce a compatible CSV export can come along for the ride - and because the export speaks ForeFlight\'s widely-supported format, most logbook apps can read what Resyrv produces.' },
    ],
    docLink: '/docs/pilot-logbook',
    docLabel: 'Docs: the pilot logbook',
  },
  {
    slug: 'airbly',
    name: 'Airbly',
    logo: '/marketing/logos/airbly.png',
    logoHeight: 20,
    category: 'Flight data',
    managedBy: 'Organization admin',
    setup: 'About 10 minutes',
    tagline: 'The airplane reports its own hours.',
    overview: [
      'Airbly is a data recorder that lives in the aircraft. It watches the engine, notices when a flight starts and ends, and sends what it saw. Connect it to Resyrv and the engine and tach time from each flight reaches your organization within about a minute of shutdown.',
      'That closes the oldest gap in shared-aircraft billing: the return readings. When a pilot opens Return on a reservation the recorder flew, Hobbs In and Tach In are already filled with the reading the recorder implies, labeled so everyone knows where the number came from. The pilot still confirms or corrects it, and nothing closes on its own. The difference is that the honest pilot no longer has to squint at a panel meter in the dark, and the guessed number no longer becomes the invoice.',
      'Resyrv checks the two numbers against each other. If the pilot enters a reading more than a tenth of an hour off what the recorder reported, it opens a meter exception with both figures side by side, so an admin can accept the pilot\'s entry or replay the billing with the corrected numbers. And if the recorder reports a flight that no reservation covers, your admins hear about it.',
    ],
    syncs: [
      'Engine and tach time from each recorded flight',
      'Return readings, pre-filled for the pilot to confirm',
      'Meter exceptions when a reading is more than 0.1 hours off',
      'Alerts when a recorded flight has no reservation',
      'Departure and arrival airports, filled in when the pilot leaves them blank',
    ],
    steps: [
      { title: 'Get your key', body: 'In Airbly, an administrator generates the group API key from group settings. Generate it once - regenerating invalidates the old one.' },
      { title: 'Connect in Resyrv', body: 'Paste the key into Organization Settings > Flight Data and connect. Recorder aircraft match to your fleet by tail number, and you can fix any mismatch on the same page.' },
      { title: 'Paste the webhook URL back', body: 'Resyrv shows a webhook URL on the card. Put it in Airbly\'s group settings and flights arrive within about a minute of shutdown. Skip it and an hourly check still catches everything up.' },
    ],
    faq: [
      { q: 'Does this close reservations automatically?', a: 'No, and that is deliberate. The recorder pre-fills the return readings; a person still reviews and submits the return. Automatic closing would put an unreviewed number on an invoice, which is the problem this integration exists to solve.' },
      { q: 'Does it overwrite our aircraft meter readings?', a: 'Never. Recorder time is added to the reservation\'s own out readings, so the aircraft\'s recorded Hobbs and tach stay exactly what your people entered.' },
      { q: 'What happens when a member flies without booking?', a: 'The recorder still reports the flight, and Resyrv notifies your admins that a flight arrived with no reservation behind it. You can turn that notification on or off under Notifications.' },
      { q: 'Does Resyrv show live aircraft positions from the recorder?', a: 'Not today. This integration covers engine and tach time, the airports the recorder saw, and the checks built on those. Position tracking is not part of it.' },
    ],
    docLink: '/docs/flight-data-recorders',
    docLabel: 'Docs: flight data recorders',
  },
  {
    slug: 'airsync',
    name: 'AirSync',
    logo: '/marketing/logos/airsync.png',
    logoHeight: 20,
    category: 'Flight data',
    managedBy: 'Organization admin',
    setup: 'About 2 minutes',
    tagline: 'Your glass panel already knows the hours.',
    overview: [
      'AirSync pulls the flight log straight out of the avionics. Where a recorder watches the engine from outside, AirSync reads what the panel wrote down, so the engine time and airborne time that reach Resyrv are the numbers the aircraft itself recorded.',
      'That closes the oldest gap in shared-aircraft billing: the return readings. When a pilot opens Return on a reservation AirSync flew, Hobbs In is already filled with the reading the log implies, labeled so everyone knows where the number came from. The pilot still confirms or corrects it, and nothing closes on its own.',
      'Resyrv checks the two numbers against each other. If the pilot enters a Hobbs reading more than a tenth of an hour off what AirSync reported, it opens a meter exception with both figures side by side, so an admin can accept the entry or replay the billing with the corrected numbers. And if AirSync reports a flight that no reservation covers, your admins hear about it.',
    ],
    syncs: [
      'Engine time and airborne time from each recorded flight',
      'Hobbs In, pre-filled for the pilot to confirm',
      'Meter exceptions when a Hobbs reading is more than 0.1 hours off',
      'Alerts when a recorded flight has no reservation',
      'Departure and arrival airports, filled in when the pilot leaves them blank',
    ],
    steps: [
      { title: 'Enable Resyrv in AirSync', body: 'In AirSync, turn Resyrv on for the aircraft you want reporting. Nothing to generate and no key to copy.' },
      { title: 'Connect in Resyrv', body: 'Open Organization Settings > Flight Data. If AirSync has already offered us your aircraft, it is one click. Otherwise type one of your tail numbers and connect. The rest of your AirSync aircraft come with it, matched to your fleet by tail number.' },
      { title: 'Nothing to paste back', body: 'Resyrv subscribes to each aircraft with AirSync directly, so flights arrive within about a minute of shutdown. An hourly check catches anything a missed push would have left behind.' },
    ],
    faq: [
      { q: 'Does AirSync report tach time?', a: 'No. AirSync reports engine time and airborne time, not tach. On an aircraft you bill by tach, Resyrv leaves the Tach In box empty for the pilot to fill in and raises no exception against it. Hobbs still pre-fills and is still checked.' },
      { q: 'Does this close reservations automatically?', a: 'No, and that is deliberate. The log pre-fills the return reading; a person still reviews and submits the return. Automatic closing would put an unreviewed number on an invoice, which is the problem this integration exists to solve.' },
      { q: 'Does it overwrite our aircraft meter readings?', a: 'Never. Recorded time is added to the reservation\'s own out readings, so the aircraft\'s recorded Hobbs and tach stay exactly what your people entered.' },
      { q: 'What if the same aircraft is in two organizations?', a: 'An AirSync account connects to one Resyrv organization. If someone has already connected an aircraft, the second attempt says so rather than quietly splitting the flights.' },
    ],
    docLink: '/docs/flight-data-recorders',
    docLabel: 'Docs: flight data recorders',
  },
]

// Integrations with a landing page. Coming-soon entries appear in the
// directory only, so nothing links to a page that does not exist.
export const LIVE_INTEGRATIONS = INTEGRATIONS.filter((i) => !i.comingSoon)

export function getIntegration(slug) {
  return LIVE_INTEGRATIONS.find((i) => i.slug === slug) || null
}

export function integrationPath(slug) {
  return `/integrations/${slug}`
}
